import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Checkbox, Select, Spin } from 'antd'
import SideMenu from '../../components/SideMenu'
import MultiFiltro from '../../components/MultiFiltro'
import api from '../../service/api'
import * as D from '../dashboard/styles'
import { RecPanel, RecTitle } from '../recebimentos/styles'
import * as S from './styles'

const { Option } = Select

/* ---------- tipos da API ---------- */

type CategoriaValor = { categoria: string; total: number }
type BancoRecPag = { banco: string; recebimentos: number; pagamentos: number }
type HistoricoMes = { mes: number; nomeMes: string; recebimentos: number; pagamentos: number }

type ExtratoData = {
  recebimentoPorCategoria: CategoriaValor[]
  recebimentoPorBanco: BancoRecPag[]
  totalRecebimentos: number
  totalPagamentos: number
  historicoPorMes: HistoricoMes[]
  pagamentosPorCategoria: CategoriaValor[]
  pagamentosTotal: number
}

type Filtros = { empresas: string[]; empresasCnpj: Record<string, string>; anos: number[] }

/* ---------- helpers ---------- */

const fmtBRL = (n: number) =>
  (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

// rotulo compacto para as barras do historico (evita valores enormes se sobreporem)
const fmtCompacto = (n: number) => {
  const abs = Math.abs(n ?? 0)
  if (abs >= 1e9) return `R$ ${(n / 1e9).toFixed(1).replace('.', ',')} bi`
  if (abs >= 1e6) return `R$ ${(n / 1e6).toFixed(1).replace('.', ',')} mi`
  if (abs >= 1e3) return `R$ ${(n / 1e3).toFixed(0)} mil`
  return (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

const MESES = [
  { v: 1, l: 'Janeiro' },
  { v: 2, l: 'Fevereiro' },
  { v: 3, l: 'Março' },
  { v: 4, l: 'Abril' },
  { v: 5, l: 'Maio' },
  { v: 6, l: 'Junho' },
  { v: 7, l: 'Julho' },
  { v: 8, l: 'Agosto' },
  { v: 9, l: 'Setembro' },
  { v: 10, l: 'Outubro' },
  { v: 11, l: 'Novembro' },
  { v: 12, l: 'Dezembro' },
]

const TODOS = 'todos'

/* ---------- componente ---------- */

const Extrato: React.FC = () => {
  // escopo por grupo (acesso do cliente via /acessar/:id -> ?grupo=NOME)
  const location = useLocation()
  const grupo = new URLSearchParams(location.search).get('grupo') || ''

  const [filtros, setFiltros] = useState<Filtros>({ empresas: [], empresasCnpj: {}, anos: [] })
  const [empresas, setEmpresas] = useState<string[]>([])
  const [anos, setAnos] = useState<string[]>([])
  const [meses, setMeses] = useState<string[]>([])

  const [data, setData] = useState<ExtratoData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    api
      .get('/DashboardExtrato/get-filtros', { params: grupo ? { grupo } : {} })
      .then((res) => {
        const d = res.data?.data ?? {}
        setFiltros({ empresas: d.empresas ?? [], empresasCnpj: d.empresasCnpj ?? {}, anos: d.anos ?? [] })
      })
      .catch(() => setFiltros({ empresas: [], empresasCnpj: {}, anos: [] }))
  }, [grupo])

  const carregar = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = {}
      if (grupo) params.NomeDoGrupo = grupo
      if (empresas.length) params.Empresa = empresas.join(',')
      if (anos.length) params.Ano = anos.join(',')
      if (meses.length) params.Mes = meses.join(',')

      const res = await api.get('/DashboardExtrato/get-dashboard-extrato', { params })
      setData(res.data?.data ?? null)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [grupo, empresas, anos, meses])

  useEffect(() => {
    carregar()
  }, [carregar])

  const recebimentoCategoria = data?.recebimentoPorCategoria ?? []
  const recebimentoBanco = data?.recebimentoPorBanco ?? []
  const historico = data?.historicoPorMes ?? []
  const pagamentosCategoria = data?.pagamentosPorCategoria ?? []

  const maxCat = Math.max(1, ...recebimentoCategoria.map((c) => c.total))
  const maxHist = Math.max(1, ...historico.flatMap((h) => [h.recebimentos, h.pagamentos]))

  return (
    <D.Page>
      <SideMenu active="extrato" />

      <D.Main>
        {loading && (
          <D.LoadingOverlay>
            <Spin size="large" tip="Carregando dados..." />
          </D.LoadingOverlay>
        )}

        <D.FiltersRow>
          <D.Filter>
            <label>Empresa Selecionada</label>
            <Select
              mode="multiple"
              allowClear
              value={empresas}
              onChange={setEmpresas}
              placeholder="Todos"
              maxTagCount="responsive"
              optionLabelProp="label"
              optionFilterProp="label"
              menuItemSelectedIcon={null}
              showArrow
              style={{ width: '100%' }}
            >
              {filtros.empresas.map((e) => {
                const cnpj = filtros.empresasCnpj?.[e] || 'Sem CNPJ'
                return (
                  <Option key={e} value={e} label={e} title={`${e} — CNPJ: ${cnpj}`}>
                    <span title={`CNPJ: ${cnpj}`}>
                      <Checkbox checked={empresas.includes(e)} style={{ marginRight: 8 }} />
                      {e}
                    </span>
                  </Option>
                )
              })}
            </Select>
          </D.Filter>
          <D.Filter>
            <label>Ano Selecionado</label>
            <MultiFiltro
              value={anos}
              onChange={setAnos}
              options={filtros.anos.map((a) => ({ value: String(a), label: String(a) }))}
            />
          </D.Filter>
          <D.Filter>
            <label>Mês Selecionado</label>
            <MultiFiltro
              value={meses}
              onChange={setMeses}
              options={MESES.map((m) => ({ value: String(m.v), label: m.l }))}
            />
          </D.Filter>
        </D.FiltersRow>

        <S.Grid4>
          {/* Recebimento por Categoria */}
          <D.Panel>
            <div className="panel-title">Recebimento por Categoria</div>
            <S.PanelScroll style={{ marginRight: -18, paddingRight: 10 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <D.AxisLabel>Categoria</D.AxisLabel>
                <div style={{ flex: 1 }}>
                  {recebimentoCategoria.map((c) => (
                    <D.HBar key={c.categoria}>
                      <div className="label">{c.categoria}</div>
                      <div className="track">
                        <div className="fill" style={{ width: `${(c.total / maxCat) * 100}%` }} />
                        <div className="amount">{fmtBRL(c.total)}</div>
                      </div>
                    </D.HBar>
                  ))}
                </div>
              </div>
            </S.PanelScroll>
          </D.Panel>

          {/* Recebimento por Banco */}
          <RecPanel>
            <RecTitle>Recebimento por Banco</RecTitle>
            <S.PanelScroll>
            <D.Table style={{ height: '100%' }}>
              <thead>
                <tr>
                  <th>Banco</th>
                  <th>Recebimentos</th>
                  <th>Pagamentos</th>
                </tr>
              </thead>
              <tbody>
                {recebimentoBanco.map((b) => (
                  <tr key={b.banco}>
                    <td>{b.banco}</td>
                    <td>{fmtBRL(b.recebimentos)}</td>
                    <td>{fmtBRL(b.pagamentos)}</td>
                  </tr>
                ))}
                <tr aria-hidden="true">
                  <td colSpan={3} style={{ height: '100%', border: 'none', padding: 0 }} />
                </tr>
                <tr className="total">
                  <td>Total</td>
                  <td>{fmtBRL(data?.totalRecebimentos ?? 0)}</td>
                  <td>{fmtBRL(data?.totalPagamentos ?? 0)}</td>
                </tr>
              </tbody>
            </D.Table>
            </S.PanelScroll>
          </RecPanel>

          {/* Histórico de Recebimentos e Pagamentos */}
          <D.Panel>
            <div className="panel-title">Histórico de Recebimentos e Pagamentos</div>
            <S.GBars>
              {historico.map((h) => (
                <S.GGroup key={h.mes}>
                  <div className="pair">
                    <div className="bar-col">
                      <span className="cap">{fmtCompacto(h.recebimentos)}</span>
                      <div className="bar blue" style={{ height: `${(h.recebimentos / maxHist) * 100}%` }} />
                    </div>
                    <div className="bar-col">
                      <span className="cap">{fmtCompacto(h.pagamentos)}</span>
                      <div className="bar yellow" style={{ height: `${(h.pagamentos / maxHist) * 100}%` }} />
                    </div>
                  </div>
                  <div className="xlabel">{h.nomeMes}</div>
                </S.GGroup>
              ))}
            </S.GBars>
            <S.Legend>
              <span>
                <i className="dot blue" />
                Recebimentos
              </span>
              <span>
                <i className="dot yellow" />
                Pagamentos
              </span>
            </S.Legend>
          </D.Panel>

          {/* Pagamentos por Categoria */}
          <RecPanel>
            <RecTitle>Pagamentos por Categoria</RecTitle>
            <S.PanelScroll>
            <D.Table style={{ height: '100%' }}>
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Valor Pago</th>
                </tr>
              </thead>
              <tbody>
                {pagamentosCategoria.map((p) => (
                  <tr key={p.categoria}>
                    <td>{p.categoria}</td>
                    <td>{fmtBRL(p.total)}</td>
                  </tr>
                ))}
                <tr aria-hidden="true">
                  <td colSpan={2} style={{ height: '100%', border: 'none', padding: 0 }} />
                </tr>
                <tr className="total">
                  <td>Total</td>
                  <td>{fmtBRL(data?.pagamentosTotal ?? 0)}</td>
                </tr>
              </tbody>
            </D.Table>
            </S.PanelScroll>
          </RecPanel>
        </S.Grid4>
      </D.Main>
    </D.Page>
  )
}

export default Extrato
